import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Undo2, Check, Eraser } from "lucide-react";
import {
  DRAW_SPACE,
  STROKE_WIDTHS,
  MINI_STROKE_WIDTHS,
  appendPoint,
  checkDrawingSize,
  cloneDrawing,
  emptyDrawing,
  isEmptyDrawing,
  maxBytesForMode,
  strokeCount,
  toNormalized,
  type Drawing,
  type DrawMode,
  type Stroke,
  type DrawEvent,
  eraseStrokes,
} from "../drawing";
import { INK_COLORS, DEFAULT_PEN, getPlayerColor, CANVAS_PAPER, type ColorSlot } from "../palette";

// ─────────────────────────────────────────────────────────────────────────────
// DrawCanvas — the pointer-events drawing surface (design §6).
//
// LAYOUT IS FIXED (requirements F19, 2026-07-27). Every drawing screen — the
// lobby avatar and the in-game prompt alike — is one vertical stack:
//
//     prompt  →  SQUARE canvas at the full column width  →  pens / widths /
//     undo  →  size + stroke meter  →  submit
//
// Nothing sits beside the canvas and nothing caps it below the column: the
// square is as wide as the device allows minus the page's own thin margin.
// `maxPx` exists only for the rare caller that genuinely needs a small preview;
// no drawing screen passes it.
//
// § F153 (2026-09-06) — THE SQUARE IS WIDTH-FIRST, AT EVERY VIEWPORT.
// Jake, on his iPhone (320 CSS px — iOS Display Zoom): *"This is even worse.
// There is a lot of wasted space on top. The drawing grid should at least reach
// the sides on a standard iPhone."*
//
// F31 (2026-07-27) used to cap the square by the height the viewport had left,
// measured at runtime, so that the pens and DONE were always above the fold.
// That trade is REVERSED and its machinery is gone: the square is ALWAYS the
// full width of the column it sits in, and vertical fit is bought by
// compressing what sits ABOVE it (the game screen's job) or, on a viewport too
// short for the tools as well, by letting the page scroll to them — which is
// F31.d's own principle ("a drawable square beats no scroll") applied one step
// further. `fitViewport` survives as what it always also meant: THIS SCREEN IS
// SHORT OF HEIGHT, so spend the tool rows tightly (see below). It no longer
// touches the square.
//
// § F152 (2026-09-06) — AND THE TOOLS STOP EATING THE SQUARE. Jake, drawing on
// his phone against prod: *"I want the drawing canvas to be bigger. The drawing
// prompt can be smaller, the color selection needs to resize according to the
// viewport to be on one row."* His phone renders at 320 CSS px, where the six
// fixed-44px pens wrapped onto a second line and the tool stack under the square
// (pens 44 + widths/undo 44 + meter 39 + DONE 48, each with its own gap) came to
// more height than the square itself. Two changes here: the pen row DIVIDES the
// column instead of wrapping in it, and on a fitted screen the size meter rides
// the widths/undo row rather than taking one of its own. See § F152 in
// docs/requirements.md for the numbers.
//
// Touch, stylus and mouse through one Pointer Events path. Points are decimated
// as they arrive, the exact serialized size is measured after every stroke, and
// submit is blocked (with a visible FULL indicator) once the cap is hit.
//
// There is NO Clear button (F9). Undo is the only way back — "that is part of
// the fun".
//
// The canvas element is the imperative fast path: strokes live in a ref and
// each new segment is stroked directly, so a fast scribble never re-renders
// React. State holds only what the chrome needs (pen, size meter, counts).
//
// `touch-action: none` + pointer capture is what stops the page scrolling or
// pinch-zooming under a drawing finger.
// ─────────────────────────────────────────────────────────────────────────────

export interface DrawCanvasProps {
  /** "mini" = avatar mode: one fixed colour, smaller canvas, 10 KB cap. */
  mode?: DrawMode;
  /** Mini mode's fixed pen — the player's dealt colour slot (or a hex). */
  color?: ColorSlot;
  /**
   * Seed the canvas (redrawing an existing icon in the lobby). Compared by
   * REFERENCE — pass a stable/memoized object, or the canvas resets on every
   * parent render.
   */
  initial?: Drawing | null;
  /** Fires after every committed stroke / undo / clear. */
  onChange?: (drawing: Drawing) => void;
  /**
   * § F198 — every stroke start, point, erase and undo, as it happens, with
   * its time (`DrawEvent`). Head To Head Doodle records these so the guesser
   * can watch the drawing being made, erasing included.
   */
  onEvent?: (event: DrawEvent) => void;
  /** Fires with a cloned, submittable drawing. */
  onSubmit?: (drawing: Drawing) => void;
  submitLabel?: string;
  /** Default true. false → the parent owns submission and reads `onChange`. */
  showSubmit?: boolean;
  /** Optional secondary action ("Skip" in the lobby icon step). */
  onSkip?: () => void;
  skipLabel?: string;
  disabled?: boolean;
  /** Submitted / in flight — locks the surface. */
  busy?: boolean;
  /** Overrides the per-mode cap. */
  maxBytes?: number;
  /** Copy above the canvas (the prompt, in Drawful). */
  prompt?: ReactNode;
  /**
   * Optional hard cap on the canvas edge, in px. Omit it — and every drawing
   * screen does (F19) — and the square fills the column it is dropped into.
   */
  maxPx?: number;
  /**
   * THIS SCREEN IS SHORT OF HEIGHT — spend the chrome tightly.
   *
   * F31 (2026-07-27) introduced it as "cap the square by the space the viewport
   * leaves". § F153 (2026-09-06) took that away — Jake: *"The drawing grid
   * should at least reach the sides"* — so the square is the column's width on
   * a fitted screen exactly as it is on any other. What is left is the tool
   * layout the fit paid for and which is still worth having on a phone: 6px
   * gaps instead of 12, and the size meter riding the widths/undo row instead
   * of taking a row of its own (§ F152).
   */
  fitViewport?: boolean;
  /**
   * § F122 (2026-08-21) — show the stroke tally beside the size meter.
   *
   * Jake, on the avatar pad: *"I'm not sure why you count strokes...is there a
   * limit to strokes?"* There is no stroke limit — the limit is BYTES, which
   * is what the meter beside it reports — so on a surface where that question
   * is the only thing the number provokes, it is turned off. The meter stays:
   * it is a real constraint and a player who fills it needs to know why their
   * next stroke will not land.
   */
  showStrokeCount?: boolean;
  /**
   * § F124 (2026-08-21) — THE PAPER'S COLOUR, when the caller owns it.
   *
   * Jake, on the avatar pad: *"The image appears in the draw area, but not the
   * background color."* An avatar is drawn ON its background, so the pad has to
   * BE that background or the player is composing against the wrong surface.
   *
   * Everywhere else this stays F8's white sheet, and F8's reasoning still
   * holds there: a coloured pen on a coloured page is how strokes go invisible.
   * On `/me` the player chose both colours themselves and sees the result in
   * the header while they draw, so it is theirs to get wrong — and the pen
   * defaults to the mark colour they picked, which cannot collide by default.
   */
  paper?: string;
  /** The pen a full-mode pad starts on. Ignored in `mini` (its pen is fixed). */
  penColor?: string;
  /**
   * § F124 — the two pen widths, when the caller wants its own.
   *
   * Jake, on the avatar pad: *"Apply the width of the big pen to the small pin
   * (small circle), and make the big pin x2 as wide as it is now."* An avatar
   * is rendered at 36px in a header, so a stroke that reads on a full canvas is
   * a hairline there. Drawful's own pads keep `STROKE_WIDTHS`.
   */
  widths?: readonly number[];
  /**
   * § F125 (2026-08-21) — offer an ERASER. Jake, on the avatar pad: *"And I
   * want an erase button so I can erase some pen marks."*
   *
   * It is a REAL eraser, not a white pen: it deletes the points it passes over
   * and splits whatever is left into the pieces that survive. A pen dipped in
   * the paper colour would look identical until the player changed their
   * background, at which point every "erased" area would reappear in the old
   * colour — an avatar carries its background with it (§ F124), so the marks
   * have to actually go.
   */
  showEraser?: boolean;
  /**
   * § F126 (2026-08-21) — hide the pen row. Jake, restructuring the avatar
   * screen: *"I get one color for the drawing and one color for the
   * background."* The two selectors under the pad ARE the palette there, so a
   * second one inside it is a second source of truth for the same thing.
   */
  showPens?: boolean;
  /**
   * § F126 — label the tools. Jake, on the avatar pad: *"Remove 'undo' text
   * from the undo button."* On a pad with two tools and no room to spare the
   * glyphs carry it; the accessible names stay.
   */
  toolLabels?: boolean;
  /**
   * § F127 (2026-08-21) — content between the canvas and the tool row. Jake:
   * *"Move the color pickers to inside the scratchpad, above the pen icons,
   * erase, and undo."* The colours belong to the pad — they are what it paints
   * with — so they sit inside it, and the caller supplies them because the pad
   * itself has no opinion about where a colour comes from.
   */
  belowCanvas?: ReactNode;
  className?: string;
}

interface Pen {
  hex: string;
  width: number;
}

/*
 * § F153 — `MIN_EDGE_PX` (F31's 184px floor) and `BOTTOM_SLACK_PX` are gone
 * with the height fit they bounded. A floor only means something when
 * something else is allowed to shrink the square, and nothing is any more: the
 * square is the column, at every viewport, and a screen too short for the
 * tools scrolls to them.
 */

export default function DrawCanvas({
  mode = "full",
  color,
  initial = null,
  onChange,
  onEvent,
  onSubmit,
  submitLabel = "Submit",
  showSubmit = true,
  onSkip,
  skipLabel = "Skip",
  disabled = false,
  busy = false,
  maxBytes,
  prompt,
  maxPx,
  fitViewport = false,
  showStrokeCount = true,
  paper: paperOverride,
  penColor,
  widths: widthsOverride,
  showEraser = false,
  showPens = true,
  toolLabels = true,
  belowCanvas,
  className = "",
}: DrawCanvasProps) {
  const mini = mode === "mini";
  const cap = maxBytes ?? maxBytesForMode(mode);
  const widths: readonly number[] = widthsOverride ?? (mini ? MINI_STROKE_WIDTHS : STROKE_WIDTHS);

  // F8 — WHITE PAD, YOUR COLOUR. An avatar is drawn on paper, in the pen the
  // engine dealt you: assigned blue ⇒ blue strokes, always. (It briefly ran the
  // other way — contrasting ink on a sheet of the player's colour — to fix
  // "coloured strokes on coloured fill = invisible". White paper solves that
  // without inverting the identity: PlayerAvatar frames the white sheet with a
  // ring of the player's colour, so the tone is still what you recognise them
  // by across the lobby.)
  const tone = useMemo(() => getPlayerColor(color ?? null), [color]);
  const paper = paperOverride ?? CANVAS_PAPER;
  const fixedColor = mini ? tone.hex : null;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef<Drawing>(initial ? cloneDrawing(initial) : emptyDrawing());
  const activeStrokeRef = useRef<Stroke | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const cssSizeRef = useRef(0);
  const paperRef = useRef(paper);
  paperRef.current = paper;

  const [penHex, setPenHex] = useState<string>(fixedColor ?? penColor ?? DEFAULT_PEN.hex);
  const [penWidth, setPenWidth] = useState<number>(widths[0]);
  /*
   * § F124 — a caller-owned pen colour follows the caller: on `/me` the mark
   * colour is a live choice, and a pad still holding the previous one would
   * hand the player the wrong pen the moment they changed their mind. A pen
   * they picked from the swatches below wins until the caller's changes again.
   */
  useEffect(() => {
    if (penColor) setPenHex(penColor);
  }, [penColor]);
  const [strokes, setStrokes] = useState(() => strokeCount(drawingRef.current));
  const [bytes, setBytes] = useState(() => checkDrawingSize(drawingRef.current, cap).bytes);
  const [drawing, setDrawing] = useState(false);
  /** § F125 — the eraser is a MODE, so the pen keeps the colour it had. */
  const [erasing, setErasing] = useState(false);
  const erasingRef = useRef(erasing);
  erasingRef.current = erasing;

  /**
   * § F125 — THE PAPER IS ALWAYS ONE OF THE PENS, when the caller owns the
   * paper. Jake: *"In the color pallet for drawing, I need one color to always
   * be the currently selected background."* On the avatar pad the background is
   * a live choice, so painting IN it is how you fill a shape or knock a corner
   * back — different from the eraser (§ F125), which removes marks rather than
   * covering them. It is prepended, so it does not shuffle the fixed six.
   */
  const pens = useMemo(() => {
    if (!paperOverride) return INK_COLORS;
    const already = INK_COLORS.some(
      (c) => c.hex.toLowerCase() === paperOverride.toLowerCase()
    );
    if (already) return INK_COLORS;
    return [{ id: "paper", name: "Background", hex: paperOverride }, ...INK_COLORS];
  }, [paperOverride]);

  const pen: Pen = { hex: fixedColor ?? penHex, width: penWidth };
  const penRef = useRef(pen);
  penRef.current = pen;

  const locked = disabled || busy;
  const over = bytes > cap;
  const fraction = Math.min(1, bytes / cap);
  const empty = strokes === 0 || isEmptyDrawing(drawingRef.current);
  const canSubmit = !locked && !empty && !over;

  /**
   * § F153 — ONE COLUMN CAP, FOR THE SQUARE AND FOR THE TOOLS ALIKE.
   *
   * F19 put the square at the column's full width; F31 gave it a second,
   * narrower cap measured off the viewport's leftover height, which is what
   * F31.c then had to defend the TOOLS from (they kept the whole column while
   * the square shrank). § F153 deletes the height cap, so the two caps are the
   * same cap again and there is nothing left to diverge: `maxPx` — the rare
   * preview caller, and no drawing screen passes it — or the column.
   *
   * The square is therefore never narrower than the column. It cannot be: no
   * code path exists that would make it so.
   */
  const columnMax: number | string = maxPx ?? "100%";

  // ── canvas plumbing ────────────────────────────────────────────────────────

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const css = cssSizeRef.current || canvas.clientWidth;
    const dpr = Math.min(3, (typeof window !== "undefined" && window.devicePixelRatio) || 1);

    if (canvas.width !== Math.round(css * dpr)) {
      canvas.width = Math.round(css * dpr);
      canvas.height = Math.round(css * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, css, css);
    ctx.fillStyle = paperRef.current;
    ctx.fillRect(0, 0, css, css);

    const k = css / DRAW_SPACE;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of drawingRef.current.strokes) {
      const n = Math.floor(s.pts.length / 2);
      if (n === 0) continue;
      ctx.strokeStyle = s.c;
      ctx.fillStyle = s.c;
      ctx.lineWidth = Math.max(1, s.w * k);
      if (n === 1) {
        ctx.beginPath();
        ctx.arc(s.pts[0] * k, s.pts[1] * k, Math.max(0.5, (s.w * k) / 2), 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(s.pts[0] * k, s.pts[1] * k);
      for (let i = 1; i < n; i++) ctx.lineTo(s.pts[i * 2] * k, s.pts[i * 2 + 1] * k);
      ctx.stroke();
    }
  }, []);

  // Keep the backing store in step with the layout box.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = () => {
      const next = Math.round(wrap.clientWidth);
      if (next > 0 && next !== cssSizeRef.current) {
        cssSizeRef.current = next;
        paint();
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [paint]);

  // Adopt a new `initial` drawing (lobby icon redraw / phase change).
  useEffect(() => {
    drawingRef.current = initial ? cloneDrawing(initial) : emptyDrawing();
    setStrokes(strokeCount(drawingRef.current));
    setBytes(checkDrawingSize(drawingRef.current, cap).bytes);
    paint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  useEffect(() => {
    if (fixedColor) setPenHex(fixedColor);
  }, [fixedColor]);

  // The dealt colour can land after the first paint; the sheet has to follow.
  useEffect(() => {
    paint();
  }, [paper, paint]);

  const commit = useCallback(() => {
    const d = drawingRef.current;
    setStrokes(strokeCount(d));
    setBytes(checkDrawingSize(d, cap).bytes);
    onChange?.(cloneDrawing(d));
  }, [cap, onChange]);

  // ── pointer handling ───────────────────────────────────────────────────────

  const pointToSpace = (clientX: number, clientY: number): [number, number] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return toNormalized(clientX, clientY, rect);
  };

  const strokeSegment = (from: [number, number], to: [number, number], s: Stroke) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const k = (cssSizeRef.current || 1) / DRAW_SPACE;
    ctx.strokeStyle = s.c;
    ctx.lineWidth = Math.max(1, s.w * k);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(from[0] * k, from[1] * k);
    ctx.lineTo(to[0] * k, to[1] * k);
    ctx.stroke();
  };

  /**
   * § F125 — rub out everything within `radius` of (x, y).
   *
   * Each stroke becomes the RUNS of points that survive, so erasing the middle
   * of a line leaves the two ends rather than deleting the whole thing. A run
   * of one point is dropped: a stroke needs two to be drawable.
   */
  const eraseAt = (x: number, y: number, radius: number): boolean => {
    // § F198 — the shared arithmetic, so a replay of the event log erases
    // exactly what the pad did.
    const next = eraseStrokes(drawingRef.current.strokes, x, y, radius);
    if (!next) return false;
    drawingRef.current.strokes = next;
    emit(["e", 0, x, y, radius]);
    return true;
  };

  /** § F198 — stamp an event with its time since the first one, and hand it on. */
  const eventClock = useRef<number | null>(null);
  const emit = (ev: DrawEvent) => {
    if (!onEvent) return;
    const now = performance.now();
    eventClock.current ??= now;
    ev[1] = Math.round(now - eventClock.current);
    onEvent(ev);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (locked || over) return;
    if (pointerIdRef.current !== null) return; // ignore a second finger mid-stroke
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();

    pointerIdRef.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Capture is a nicety; drawing still works without it.
    }

    const [x, y] = pointToSpace(e.clientX, e.clientY);

    // § F125 — erasing borrows the same gesture; it just removes instead of adds.
    if (erasingRef.current) {
      setDrawing(true);
      if (eraseAt(x, y, penRef.current.width)) {
        paint();
      }
      return;
    }

    const s: Stroke = { c: penRef.current.hex, w: penRef.current.width, pts: [] };
    appendPoint(s, x, y);
    drawingRef.current.strokes.push(s);
    emit(["s", 0, s.c, s.w, s.pts[0]!, s.pts[1]!]);
    activeStrokeRef.current = s;
    setDrawing(true);

    // Paint the initial dot so a tap leaves a mark immediately.
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      const k = (cssSizeRef.current || 1) / DRAW_SPACE;
      ctx.fillStyle = s.c;
      ctx.beginPath();
      ctx.arc(x * k, y * k, Math.max(0.5, (s.w * k) / 2), 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (erasingRef.current) {
      if (pointerIdRef.current !== e.pointerId) return;
      e.preventDefault();
      const [x, y] = pointToSpace(e.clientX, e.clientY);
      if (eraseAt(x, y, penRef.current.width)) paint();
      return;
    }
    const s = activeStrokeRef.current;
    if (!s || pointerIdRef.current !== e.pointerId) return;
    e.preventDefault();

    // Coalesced events recover the points the browser batched between frames.
    const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    let events: { clientX: number; clientY: number }[] = [e];
    try {
      const coalesced = native.getCoalescedEvents?.();
      if (coalesced && coalesced.length > 0) events = coalesced;
    } catch {
      // Not supported — the single event is plenty.
    }

    for (const ev of events) {
      const prev: [number, number] = [s.pts[s.pts.length - 2], s.pts[s.pts.length - 1]];
      const [x, y] = pointToSpace(ev.clientX, ev.clientY);
      if (!appendPoint(s, x, y)) continue; // live decimation
      emit(["p", 0, s.pts[s.pts.length - 2]!, s.pts[s.pts.length - 1]!]);
      strokeSegment(prev, [x, y], s);
    }
  };

  const endStroke = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    pointerIdRef.current = null;
    activeStrokeRef.current = null;
    setDrawing(false);
    commit();
  };

  // ── tools ──────────────────────────────────────────────────────────────────

  const undo = () => {
    if (locked || drawingRef.current.strokes.length === 0) return;
    drawingRef.current.strokes.pop();
    emit(["u", 0]);
    paint();
    commit();
  };

  const submit = () => {
    if (!canSubmit) return;
    onSubmit?.(cloneDrawing(drawingRef.current));
  };

  // ── chrome ─────────────────────────────────────────────────────────────────

  const surfaceStyle: CSSProperties = {
    touchAction: "none",
    overscrollBehavior: "contain",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    cursor: locked ? "not-allowed" : "crosshair",
  };

  const meterColor = over ? "#FF4500" : fraction >= 0.85 ? "#E06A00" : "#000000";

  /*
   * Size meter — the "full" indicator that gates submit.
   *
   * The track has to clear the 4px ink border on BOTH edges before any of it is
   * track: at h-3 the 12px box was 8px of border and a 4px slot, so an empty
   * meter read as a solid black rule. h-5 leaves a real 12px channel, which is
   * what makes 0% look empty and 40% look like 40%.
   *
   * § F152 — it is a VALUE now rather than a block, because on a fitted screen
   * it rides the tool row instead of taking a row of its own (see below).
   */
  const meter = (
    <>
      <div className="h-5 w-full ink-border rounded-full bg-white overflow-hidden" aria-hidden="true">
        <div
          className="h-full transition-[width] duration-150"
          style={{ width: `${Math.round(fraction * 100)}%`, background: meterColor }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between gap-1 font-heading font-extrabold uppercase tracking-wide leading-none text-[10px]">
        <span className={over ? "text-brand-secondary" : "text-black/70"}>
          {over ? "Full — undo a stroke" : `${Math.round(fraction * 100)}% of ${Math.round(cap / 1024)} KB`}
        </span>
        {showStrokeCount && (
          <span className="text-black/70 shrink-0">
            {strokes} {strokes === 1 ? "stroke" : "strokes"}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div
      /* F31/§ F152 — a fitted stack spends 6px gaps instead of 12: four gaps
         under a square that is now the whole column (§ F153) are four gaps of
         scroll on a short phone. */
      className={`w-full flex flex-col items-center ${fitViewport ? "gap-1.5" : "gap-3"} ${className}`}
    >
      {prompt && (
        <div className="w-full text-center font-heading font-extrabold uppercase tracking-wide text-base">
          {prompt}
        </div>
      )}

      <div ref={wrapRef} className="w-full" style={{ maxWidth: columnMax }}>
        <div
          className="relative w-full ink-border rounded-block block-shadow-1 bg-white overflow-hidden"
          style={{ aspectRatio: "1 / 1" }}
        >
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={surfaceStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onLostPointerCapture={endStroke}
            onContextMenu={(e) => e.preventDefault()}
            aria-label={mini ? "Draw your avatar" : "Drawing canvas"}
            role="img"
          />
          {empty && !drawing && !locked && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span
                className="font-heading font-extrabold uppercase tracking-wide text-sm text-center px-4 opacity-60"
                style={{ color: "#000000" }}
              >
                {mini ? "Draw your avatar" : "Draw here"}
              </span>
            </div>
          )}
          {busy && (
            <div className="pointer-events-none absolute inset-0 bg-white/60 flex items-center justify-center">
              <span className="font-heading font-extrabold uppercase tracking-wide text-sm">Sent</span>
            </div>
          )}
        </div>
      </div>

      {belowCanvas && (
        <div className="w-full" style={{ maxWidth: columnMax }}>
          {belowCanvas}
        </div>
      )}

      {/* Pens — full mode only, and only when the caller has not taken them over.
          § F152 (2026-09-06) — ONE ROW, SIZED BY THE VIEWPORT. Jake, on his
          phone: *"the color selection needs to resize according to the viewport
          to be on one row."* The swatches were a fixed 44px in a `flex-wrap`
          row, which fits six pens down to a ~304px column and then folds the
          sixth onto a line of its own — 52px of height stolen from the canvas
          on exactly the narrow phone that could least afford it (his renders at
          320 CSS px, where the column is 296). They are `flex-1 basis-0` now
          with a square aspect: the row divides the column it is given, so six
          pens (or the seven the avatar pad's paper swatch makes) always share
          ONE line. `max-w-[56px]` stops them ballooning on a tablet and
          `min-w-[38px]` keeps a real touch target — 42.6px at 320, 49 at 360,
          54 at 390 — so the wrap can only come back if somebody puts a seventh
          control in here, which is what the guard forbids. */}
      {!mini && showPens && (
        <div
          className="w-full flex items-center justify-center gap-2"
          style={{ maxWidth: columnMax }}
          data-testid="draw-pen-row"
        >
          {pens.map((c) => {
            const active = pen.hex === c.hex;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setPenHex(c.hex);
                  setErasing(false);
                }}
                disabled={locked}
                aria-label={`${c.name} pen`}
                aria-pressed={active}
                className={`flex-1 basis-0 min-w-[38px] max-w-[56px] aspect-square rounded-block ink-border btn-press btn-off ${
                  active ? "block-shadow-1 -translate-y-0.5" : ""
                }`}
                style={{ background: c.hex }}
              />
            );
          })}
        </div>
      )}

      {/* Widths + undo. No Clear — F9: undo only, "that is part of the fun". */}
      <div className="w-full flex items-center justify-center gap-2" style={{ maxWidth: columnMax }}>
        {widths.map((w, wi) => {
          const active = pen.width === w;
          return (
            <button
              key={w}
              type="button"
              onClick={() => {
                setPenWidth(w);
                setErasing(false);
              }}
              disabled={locked}
              aria-label={wi === 0 ? "Thin pen" : "Thick pen"}
              aria-pressed={active}
              // The swatch sits on the paper it will actually mark — white, in
              // both modes since F8 — so the dot previews the real stroke.
              className={`h-11 w-11 rounded-block ink-border btn-press flex items-center justify-center btn-off ${
                active ? "block-shadow-1 -translate-y-0.5" : ""
              }`}
              style={{ background: paper }}
            >
              <span
                className="rounded-full"
                style={{
                  background: pen.hex,
                  width: 8 + Math.round((w / widths[widths.length - 1]) * 12),
                  height: 8 + Math.round((w / widths[widths.length - 1]) * 12),
                }}
              />
            </button>
          );
        })}

        {/* § F125 — the eraser, where the hand already is. It is a MODE: the
            pen keeps its colour and its width (the width is the eraser's
            radius), and the next tap on a width or a colour turns it off, so
            there is no way to be stuck in it. */}
        {showEraser && (
          <button
            type="button"
            onClick={() => setErasing((on) => !on)}
            disabled={locked}
            aria-label="Eraser"
            aria-pressed={erasing}
            data-testid="draw-eraser"
            className={`h-11 px-3 rounded-block ink-border btn-press flex items-center gap-1 font-heading font-extrabold text-xs uppercase btn-off ${
              erasing ? "bg-brand-secondary text-white block-shadow-1 -translate-y-0.5" : "bg-white"
            }`}
          >
            <Eraser size={18} strokeWidth={2.5} />
            {toolLabels && <span>Erase</span>}
          </button>
        )}

        <button
          type="button"
          onClick={undo}
          disabled={locked || strokes === 0}
          aria-label="Undo last stroke"
          className="h-11 px-3 rounded-block ink-border bg-white btn-press flex items-center gap-1 font-heading font-extrabold text-xs uppercase btn-off"
        >
          <Undo2 size={18} strokeWidth={2.5} />
          {toolLabels && <span>Undo</span>}
        </button>

        {/* § F152 — ON A FITTED SCREEN THE METER RIDES THIS ROW. A 20px bar and
            its 10px caption are 39px of a 44px row that is already there, so
            giving them a row of their own spent 47px (the row plus a gap) of a
            viewport the canvas was losing to. It stays a full-width block on
            every screen that is NOT fighting for height — the avatar pad has
            the whole column and no reason to crowd it. */}
        {fitViewport && <div className="flex-1 min-w-0">{meter}</div>}
      </div>

      {!fitViewport && (
        <div className="w-full" style={{ maxWidth: columnMax }}>
          {meter}
        </div>
      )}

      {(showSubmit || onSkip) && (
        <div className="w-full flex items-center gap-2" style={{ maxWidth: columnMax }}>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={locked}
              className="min-h-[48px] px-4 rounded-block ink-border bg-white btn-press font-heading font-extrabold uppercase tracking-wide text-sm btn-off"
            >
              {skipLabel}
            </button>
          )}
          {showSubmit && (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="flex-1 min-h-[48px] rounded-block ink-border block-shadow-1 btn-press bg-brand-primary font-heading font-extrabold uppercase tracking-wide text-base flex items-center justify-center gap-2 btn-off"
            >
              <Check size={20} strokeWidth={3} />
              {busy ? "Sent" : submitLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
